/**
 * The IP-drift RE-CHECK wire (testmap #13, owner bug ipdrift-recheck-200826-001 — the be's landed
 * contract, `IpDriftRecheckController`, be `0a2140e`):
 *
 *   GET  /api/v1/sessions/{id}/ipdrift:pending   → { request: RecheckRequest | null }
 *        The officer's pending re-check request (lazy-expired server-side; 120s TTL — an
 *        unanswered re-check is an honest recorded outcome, never a silent PASS).
 *   POST /api/v1/sessions/{id}/ipdrift:capture   → the RECHECK envelope (append-only run)
 *        The droom's ANSWER. The body is EMPTY by design: the be resolves THIS request's source IP
 *        at the edge (XFF walked past trusted hops — never a client-assertable field) and runs the
 *        RECHECK comparison against the session-baseline customer IP. 404 = no pending request
 *        (expired / already answered / none) — a calm no-op for this client.
 *
 * PASSIVE class (SPEC-06 §3.3): zero customer-visible interaction, interrupts nothing. The whole
 * "capture" is one authed request from the customer's device — the freshest possible observation
 * of the network it is on RIGHT NOW, which is exactly what the old stored-state re-check lied
 * about. Nothing here reads location, camera, or anything else on-device.
 *
 * Mock mode (no backend): no re-check channel exists — pending resolves null and capture no-ops
 * (nothing pretends an officer asked).
 */

import { isMockMode, request } from './client';

export interface IpdriftRecheckRequest {
  readonly id: string;
  /** PENDING | COMPLETED | EXPIRED — the droom acts only on PENDING. */
  readonly state: string;
  readonly requestedBy: string | null;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly completedInvocationId: string | null;
}

/** The pending officer re-check request, or null (honest none / mock mode). */
export async function fetchPendingIpdriftRecheck(
  sessionId: string,
): Promise<IpdriftRecheckRequest | null> {
  if (isMockMode()) return null;
  const res = await request<{ request: IpdriftRecheckRequest | null }>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/ipdrift:pending`,
  );
  return res.request ?? null;
}

/**
 * Answer the pending re-check — the fresh network capture IS this POST. Throws ApiError
 * (404 = the request went terminal meanwhile — the caller treats it as answered-elsewhere).
 */
export function captureIpdriftRecheck(sessionId: string): Promise<unknown> {
  if (isMockMode()) return Promise.resolve(null);
  return request<unknown>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/ipdrift:capture`,
    { method: 'POST' },
  );
}
